/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ArchiveBoxIcon,
  ClockIcon,
  BellAlertIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { monitoringService, ModelMonitor, PerformanceSnapshot } from '../../services/registryApi';
import toast from 'react-hot-toast';

/* ── Status badge ─────────────────────────────────────────────────────── */

const statusColors: Record<string, string> = {
  active: 'bg-green-500/20 text-green-400',
  paused: 'bg-yellow-500/20 text-yellow-400',
  archived: 'bg-gray-700 text-gray-400',
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`px-2.5 py-1 text-xs font-medium rounded ${statusColors[status] || statusColors.archived}`}>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </span>
);

/* ── Time range options ───────────────────────────────────────────────── */

interface TimeRange {
  label: string;
  hours: number;
}

const TIME_RANGES: TimeRange[] = [
  { label: 'Last 1h', hours: 1 },
  { label: 'Last 6h', hours: 6 },
  { label: 'Last 24h', hours: 24 },
  { label: 'Last 7d', hours: 168 },
  { label: 'Last 30d', hours: 720 },
];

/* ── Simple bar chart component ───────────────────────────────────────── */

interface BarChartProps {
  data: { label: string; value: number }[];
  color: string;
  maxValue?: number;
}

const SimpleBarChart: React.FC<BarChartProps> = ({ data, color, maxValue: explicitMax }) => {
  const max = explicitMax ?? Math.max(...data.map((d) => d.value), 1);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-xs text-gray-500">
        No data available
      </div>
    );
  }

  return (
    <div className="flex items-end gap-px h-32">
      {data.map((d, i) => {
        const height = max > 0 ? (d.value / max) * 100 : 0;
        return (
          <div
            key={i}
            className="flex-1 min-w-0 group relative"
            style={{ height: '100%' }}
          >
            <div
              className="absolute bottom-0 left-0 right-0 rounded-t-sm transition-all"
              style={{
                height: `${Math.max(height, 1)}%`,
                backgroundColor: color,
                opacity: 0.8,
              }}
            />
            {/* Tooltip on hover */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
              <div className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white whitespace-nowrap shadow-lg">
                <div className="font-medium">{d.value.toFixed(2)}</div>
                <div className="text-gray-400 text-[10px]">{d.label}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── Multi-line bar chart (for latency P50/P95/P99) ──────────────────── */

interface MultiLineChartProps {
  data: { label: string; values: { key: string; value: number; color: string }[] }[];
}

const MultiLineChart: React.FC<MultiLineChartProps> = ({ data }) => {
  const allValues = data.flatMap((d) => d.values.map((v) => v.value));
  const max = Math.max(...allValues, 1);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-xs text-gray-500">
        No data available
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-end gap-px h-32">
        {data.map((d, i) => (
          <div key={i} className="flex-1 min-w-0 flex items-end gap-[1px] group relative" style={{ height: '100%' }}>
            {d.values.map((v) => {
              const height = max > 0 ? (v.value / max) * 100 : 0;
              return (
                <div
                  key={v.key}
                  className="flex-1 rounded-t-sm"
                  style={{
                    height: `${Math.max(height, 1)}%`,
                    backgroundColor: v.color,
                    opacity: 0.8,
                  }}
                />
              );
            })}
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
              <div className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white whitespace-nowrap shadow-lg">
                {d.values.map((v) => (
                  <div key={v.key} className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: v.color }} />
                    <span className="text-gray-400">{v.key}:</span>
                    <span>{v.value.toFixed(1)} ms</span>
                  </div>
                ))}
                <div className="text-gray-500 text-[10px] mt-0.5">{d.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-3 justify-end">
        {data[0]?.values.map((v) => (
          <div key={v.key} className="flex items-center gap-1 text-[10px] text-gray-400">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: v.color }} />
            {v.key}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Chart Card wrapper ───────────────────────────────────────────────── */

const ChartCard: React.FC<{
  title: string;
  currentValue: string;
  children: React.ReactNode;
}> = ({ title, currentValue, children }) => (
  <div className="bg-gray-900 border border-gray-700 rounded-xl p-5">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-medium text-gray-300">{title}</h4>
      <span className="text-lg font-semibold text-white">{currentValue}</span>
    </div>
    {children}
  </div>
);

/* ── Format helpers ───────────────────────────────────────────────────── */

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (iso: string) => new Date(iso).toLocaleString();

/* ── Main Component ───────────────────────────────────────────────────── */

const MonitorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedRange, setSelectedRange] = useState<TimeRange>(TIME_RANGES[2]); // 24h default

  /* Fetch monitor info */
  const { data: monitorData, isLoading: monitorLoading } = useQuery({
    queryKey: ['monitor', id],
    queryFn: () => monitoringService.get(id!),
    enabled: !!id,
  });

  const monitor: ModelMonitor | null = monitorData?.data?.monitor ?? null;

  /* Compute time range params */
  const timeParams = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - selectedRange.hours * 60 * 60 * 1000);
    return { start: start.toISOString(), end: end.toISOString() };
  }, [selectedRange]);

  /* Fetch metrics snapshots */
  const { data: metricsData, isLoading: metricsLoading } = useQuery({
    queryKey: ['monitor-metrics', id, selectedRange.hours],
    queryFn: () => monitoringService.getMetrics(id!, { start: timeParams.start, end: timeParams.end, limit: 100 }),
    enabled: !!id,
  });

  const snapshots: PerformanceSnapshot[] = metricsData?.data?.snapshots ?? [];

  /* Archive mutation */
  const archiveMutation = useMutation({
    mutationFn: () => monitoringService.archive(id!),
    onSuccess: () => {
      toast.success('Monitor archived');
      queryClient.invalidateQueries({ queryKey: ['monitors'] });
      navigate('/registry/monitoring');
    },
    onError: () => {
      toast.error('Failed to archive monitor');
    },
  });

  const handleArchive = () => {
    if (!window.confirm('Archive this monitor? This cannot be undone.')) return;
    archiveMutation.mutate();
  };

  /* Derive chart data from snapshots */
  const latencyChartData = snapshots.map((s) => ({
    label: fmtTime(s.window_start),
    values: [
      { key: 'P50', value: s.p50_latency_ms ?? 0, color: '#3B82F6' },
      { key: 'P95', value: s.p95_latency_ms ?? 0, color: '#F59E0B' },
      { key: 'P99', value: s.p99_latency_ms ?? 0, color: '#EF4444' },
    ],
  }));

  const throughputChartData = snapshots.map((s) => ({
    label: fmtTime(s.window_start),
    value: s.request_count ?? 0,
  }));

  const errorRateChartData = snapshots.map((s) => ({
    label: fmtTime(s.window_start),
    value: (s.error_rate ?? 0) * 100,
  }));

  const accuracyChartData = snapshots
    .filter((s) => s.accuracy != null)
    .map((s) => ({
      label: fmtTime(s.window_start),
      value: (s.accuracy ?? 0) * 100,
    }));

  const latestSnapshot = snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;

  /* Loading state */
  if (monitorLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (!monitor) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Monitor not found</p>
          <button
            onClick={() => navigate('/registry/monitoring')}
            className="text-orange-400 hover:text-orange-300 text-sm"
          >
            Back to Monitoring
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/registry/monitoring')}
              className="p-1.5 text-gray-400 hover:text-white rounded transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-white">{monitor.name}</h1>
                <StatusBadge status={monitor.status} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to={`/registry/monitoring/${id}/alerts`}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white border border-gray-700 hover:border-gray-600 rounded-lg transition-colors"
            >
              <BellAlertIcon className="w-4 h-4" />
              Alerts
            </Link>
            <button
              onClick={handleArchive}
              disabled={archiveMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 border border-gray-700 hover:border-red-500/50 rounded-lg transition-colors disabled:opacity-50"
            >
              <ArchiveBoxIcon className="w-4 h-4" />
              {archiveMutation.isPending ? 'Archiving...' : 'Archive'}
            </button>
          </div>
        </div>

        {/* Info card */}
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Model ID</p>
              <p className="text-sm text-white font-mono truncate">{monitor.model_id}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Model Version</p>
              <p className="text-sm text-white font-mono truncate">{monitor.model_version_id}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Endpoint URL</p>
              <p className="text-sm text-white truncate">{monitor.endpoint_url || '--'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Window Size</p>
              <p className="text-sm text-white">{monitor.window_size}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Retention</p>
              <p className="text-sm text-white">{monitor.retention_days} days</p>
            </div>
          </div>
        </div>

        {/* Time range selector */}
        <div className="flex items-center gap-2 mb-6">
          <ClockIcon className="w-4 h-4 text-gray-500" />
          <span className="text-xs text-gray-500 mr-1">Time Range:</span>
          {TIME_RANGES.map((range) => (
            <button
              key={range.hours}
              onClick={() => setSelectedRange(range)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                selectedRange.hours === range.hours
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                  : 'text-gray-400 hover:text-white border border-gray-700 hover:border-gray-600'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>

        {/* Charts grid */}
        {metricsLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Latency chart */}
              <ChartCard
                title="Latency (P50 / P95 / P99)"
                currentValue={
                  latestSnapshot
                    ? `${(latestSnapshot.p99_latency_ms ?? 0).toFixed(1)} ms P99`
                    : '--'
                }
              >
                <MultiLineChart data={latencyChartData} />
              </ChartCard>

              {/* Throughput chart */}
              <ChartCard
                title="Throughput (requests per window)"
                currentValue={
                  latestSnapshot
                    ? `${(latestSnapshot.request_count ?? 0).toLocaleString()} req`
                    : '--'
                }
              >
                <SimpleBarChart
                  data={throughputChartData}
                  color="#F97316"
                />
              </ChartCard>

              {/* Error rate chart */}
              <ChartCard
                title="Error Rate (%)"
                currentValue={
                  latestSnapshot
                    ? `${((latestSnapshot.error_rate ?? 0) * 100).toFixed(2)}%`
                    : '--'
                }
              >
                <SimpleBarChart
                  data={errorRateChartData}
                  color="#EF4444"
                  maxValue={100}
                />
              </ChartCard>

              {/* Accuracy chart */}
              <ChartCard
                title="Accuracy (%)"
                currentValue={
                  latestSnapshot?.accuracy != null
                    ? `${(latestSnapshot.accuracy * 100).toFixed(2)}%`
                    : '--'
                }
              >
                {accuracyChartData.length > 0 ? (
                  <SimpleBarChart
                    data={accuracyChartData}
                    color="#10B981"
                    maxValue={100}
                  />
                ) : (
                  <div className="flex items-center justify-center h-32 text-xs text-gray-500">
                    No ground truth data available
                  </div>
                )}
              </ChartCard>
            </div>

            {/* Snapshots table */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-700">
                <h3 className="text-sm font-semibold text-white">Recent Snapshots</h3>
              </div>
              {snapshots.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-gray-500">
                  No snapshots in the selected time range.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Window</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Requests</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Errors</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Error Rate</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Avg Lat.</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">P50</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">P95</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">P99</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Accuracy</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {[...snapshots].reverse().map((s) => (
                        <tr key={s.id} className="hover:bg-gray-800/50">
                          <td className="px-4 py-3 text-xs text-gray-300 whitespace-nowrap">
                            {fmtDate(s.window_start)}
                          </td>
                          <td className="px-4 py-3 text-xs text-white text-right">
                            {(s.request_count ?? 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-xs text-white text-right">
                            {(s.error_count ?? 0).toLocaleString()}
                          </td>
                          <td className={`px-4 py-3 text-xs text-right ${(s.error_rate ?? 0) >= 0.05 ? 'text-red-400' : 'text-white'}`}>
                            {((s.error_rate ?? 0) * 100).toFixed(2)}%
                          </td>
                          <td className="px-4 py-3 text-xs text-white text-right">
                            {(s.avg_latency_ms ?? 0).toFixed(1)} ms
                          </td>
                          <td className="px-4 py-3 text-xs text-white text-right">
                            {(s.p50_latency_ms ?? 0).toFixed(1)} ms
                          </td>
                          <td className="px-4 py-3 text-xs text-white text-right">
                            {(s.p95_latency_ms ?? 0).toFixed(1)} ms
                          </td>
                          <td className={`px-4 py-3 text-xs text-right ${(s.p99_latency_ms ?? 0) >= 500 ? 'text-yellow-400' : 'text-white'}`}>
                            {(s.p99_latency_ms ?? 0).toFixed(1)} ms
                          </td>
                          <td className="px-4 py-3 text-xs text-white text-right">
                            {s.accuracy != null ? `${(s.accuracy * 100).toFixed(2)}%` : '--'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MonitorDetail;
